import { buildApiUrl } from './api';

export const MAX_STORE_CHECKOUT_QUANTITY = 10;
export const STORE_CHECKOUT_MODE_PAYMENT_LINK = 'payment-link';

const STORE_CHECKOUT_MODE_HOSTED_SESSION = 'hosted-session';
const STORE_PAYMENT_LINK_RETURN_STORAGE_KEY = 'encreage.store.payment-link-return';
const STORE_PAYMENT_LINK_RETURN_MAX_AGE_MS = 1000 * 60 * 60 * 6;

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asArray(value, fallback = []) {
  return Array.isArray(value) ? value : fallback;
}

function toText(value, fallback = '') {
  if (typeof value === 'string') {
    return value;
  }

  if (value == null) {
    return fallback;
  }

  return String(value);
}

function getStoreCheckoutStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

const currencyFormatterCache = new Map();

function buildCurrencyFormatter(currencyCode = 'USD') {
  const normalizedCurrency = toText(currencyCode, 'USD').trim().toUpperCase() || 'USD';

  if (!currencyFormatterCache.has(normalizedCurrency)) {
    currencyFormatterCache.set(
      normalizedCurrency,
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: normalizedCurrency,
      }),
    );
  }

  return currencyFormatterCache.get(normalizedCurrency);
}

function formatMoney(amountInCents, currencyCode) {
  return buildCurrencyFormatter(currencyCode).format((toNumber(amountInCents) ?? 0) / 100);
}

function toBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value > 0;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase().replace(/[_-]+/g, ' ');

    if (!normalizedValue) {
      return false;
    }

    if (['false', '0', 'no', 'sold out', 'out of stock', 'unavailable'].includes(normalizedValue)) {
      return false;
    }

    if (['true', '1', 'yes', 'in stock', 'available'].includes(normalizedValue)) {
      return true;
    }
  }

  return Boolean(value);
}

function toSlug(value) {
  return toText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isAbsoluteUrl(value) {
  return /^https?:\/\//i.test(value);
}

export function isStripeHostedCheckoutUrl(value) {
  const normalizedValue = toText(value).trim();

  if (!normalizedValue) {
    return false;
  }

  try {
    const checkoutUrl = new URL(normalizedValue);
    const hostname = checkoutUrl.hostname.toLowerCase();

    return hostname === 'buy.stripe.com' || hostname === 'pay.stripe.com';
  } catch {
    return false;
  }
}

function resolveApiBackedUrl(value) {
  const normalizedValue = toText(value).trim();

  if (
    !normalizedValue ||
    isAbsoluteUrl(normalizedValue) ||
    normalizedValue.startsWith('data:') ||
    normalizedValue.startsWith('blob:')
  ) {
    return normalizedValue;
  }

  if (normalizedValue.startsWith('/api/')) {
    return buildApiUrl(normalizedValue.slice(4));
  }

  if (normalizedValue.startsWith('/')) {
    return normalizedValue;
  }

  return normalizedValue;
}

function resolveImage(value) {
  if (typeof value === 'string') {
    return resolveApiBackedUrl(value);
  }

  const image = asObject(value);

  return resolveApiBackedUrl(image.url ?? image.src ?? image.href);
}

function uniqueValues(values = []) {
  return [...new Set(values.filter(Boolean))];
}

function resolveTextList(value) {
  return asArray(value)
    .map((entry) => {
      if (typeof entry === 'string') {
        return entry.trim();
      }

      const item = asObject(entry);

      return toText(item.name ?? item.label ?? item.title).trim();
    })
    .filter(Boolean);
}

function resolveSlug(productData) {
  return toSlug(productData.slug ?? productData.Slug ?? productData.handle ?? productData.name ?? productData.id);
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.replace(/,/g, '').trim();

    if (!normalizedValue) {
      return null;
    }

    const directNumber = Number(normalizedValue);

    if (Number.isFinite(directNumber)) {
      return directNumber;
    }

    const matchedNumber = normalizedValue.match(/-?\d+(?:\.\d+)?/);

    if (matchedNumber) {
      const parsedNumber = Number(matchedNumber[0]);

      if (Number.isFinite(parsedNumber)) {
        return parsedNumber;
      }
    }
  }

  return null;
}

function resolvePrice(productData, pricing) {
  return toText(
    pricing.display ??
      pricing.formatted ??
      pricing.formattedPrice ??
      productData.priceDisplay ??
      productData.displayPrice ??
      pricing.price ??
      productData.price,
  );
}

function resolvePriceValue(productData, pricing, resolvedPrice) {
  const priceCandidates = [
    pricing.amount,
    pricing.value,
    pricing.unitAmount,
    pricing.unit_amount,
    pricing.price,
    productData.price,
    productData.amount,
    productData.value,
    resolvedPrice,
  ];

  for (const candidate of priceCandidates) {
    const parsedNumber = toNumber(candidate);

    if (parsedNumber != null) {
      return parsedNumber;
    }
  }

  return null;
}

function resolveInStock(productData, inventory) {
  const quantity = Number(
    inventory.count ??
      inventory.quantity ??
      inventory.stock ??
      productData.quantity ??
      productData.stock
  );

  if (Number.isFinite(quantity)) {
    return quantity > 0;
  }

  const availability = inventory.inStock ?? inventory.available ?? productData.inStock ?? productData.available;

  if (availability != null) {
    return toBoolean(availability);
  }

  return toBoolean(inventory.status ?? inventory.state ?? productData.status);
}

function resolveCheckoutUrl(productData, pricing) {
  return toText(
    productData.checkoutUrl ??
      productData.checkout_url ??
      productData.url ??
      productData.href ??
      pricing.checkoutUrl ??
      pricing.url,
  );
}

function resolveGallery(media, featuredImage) {
  const galleryImages = asArray(media.gallery, asArray(media.images, asArray(media.items))).map(resolveImage);

  return uniqueValues([featuredImage, ...galleryImages]);
}

function unwrapStorePayload(payload) {
  if (Array.isArray(payload)) {
    return {
      meta: {},
      products: payload,
    };
  }

  const root = asObject(payload);
  const nestedData = asObject(root.data);

  return {
    meta: asObject(root.meta ?? nestedData.meta),
    products: asArray(root.products, asArray(nestedData.products)),
  };
}

function unwrapStoreProductPayload(payload) {
  const root = asObject(payload);
  const nestedData = asObject(root.data);
  const product = normalizeStoreProduct(root.product ?? nestedData.product);
  const relatedProducts = asArray(root.relatedProducts, asArray(nestedData.relatedProducts))
    .map((item) => normalizeStoreProduct(item))
    .filter(Boolean);

  return {
    meta: asObject(root.meta ?? nestedData.meta),
    product,
    relatedProducts,
  };
}

function normalizeStoreCheckoutOrderItem(item, fallbackCurrencyCode = 'USD') {
  const itemData = asObject(item);
  const currencyCode = toText(itemData.currencyCode, fallbackCurrencyCode).toUpperCase();
  const unitPriceCents = toNumber(itemData.unitPriceCents) ?? 0;
  const totalPriceCents = toNumber(itemData.totalPriceCents) ?? 0;

  return {
    id: toText(itemData.id),
    productId: toText(itemData.productId),
    productSlug: toText(itemData.productSlug),
    productName: toText(itemData.productName, 'Store item'),
    quantity: toNumber(itemData.quantity) ?? 0,
    unitPriceCents,
    totalPriceCents,
    currencyCode,
    unitPriceDisplay: toText(itemData.unitPriceDisplay, formatMoney(unitPriceCents, currencyCode)),
    totalPriceDisplay: toText(itemData.totalPriceDisplay, formatMoney(totalPriceCents, currencyCode)),
  };
}

function normalizeStoreCheckoutOrder(order) {
  const orderData = asObject(order);
  const currencyCode = toText(orderData.currencyCode, 'USD').toUpperCase();
  const totals = asObject(orderData.totals);
  const subtotalCents = toNumber(totals.subtotalCents) ?? 0;
  const taxCents = toNumber(totals.taxCents) ?? 0;
  const shippingCents = toNumber(totals.shippingCents) ?? 0;
  const totalCents = toNumber(totals.totalCents) ?? subtotalCents + taxCents + shippingCents;

  return {
    id: toText(orderData.id),
    status: toText(orderData.status, 'pending'),
    customerEmail: toText(orderData.customerEmail) || null,
    currencyCode,
    totals: {
      subtotalCents,
      taxCents,
      shippingCents,
      totalCents,
      subtotalDisplay: toText(totals.subtotalDisplay, formatMoney(subtotalCents, currencyCode)),
      taxDisplay: toText(totals.taxDisplay, formatMoney(taxCents, currencyCode)),
      shippingDisplay: toText(totals.shippingDisplay, formatMoney(shippingCents, currencyCode)),
      totalDisplay: toText(totals.totalDisplay, formatMoney(totalCents, currencyCode)),
    },
    stripeCheckoutSessionId: toText(orderData.stripeCheckoutSessionId) || null,
    stripePaymentIntentId: toText(orderData.stripePaymentIntentId) || null,
    createdAt: toText(orderData.createdAt),
    updatedAt: toText(orderData.updatedAt),
    paidAt: toText(orderData.paidAt) || null,
    lastError: toText(orderData.lastError) || null,
    items: asArray(orderData.items)
      .map((item) => normalizeStoreCheckoutOrderItem(item, currencyCode))
      .filter(Boolean),
  };
}

export function normalizeStoreRouteSlug(routeSlug = '') {
  return toSlug(routeSlug);
}

export function normalizeStoreProduct(product) {
  if (!product || typeof product !== 'object' || Array.isArray(product)) {
    return null;
  }

  const productData = asObject(product);
  const media = asObject(productData.media);
  const pricing = asObject(productData.pricing);
  const inventory = asObject(productData.inventory);
  const slug = resolveSlug(productData);
  const image = resolveImage(media.featured) || resolveImage(productData.image);
  const shortDescription = toText(productData.shortDescription ?? productData.summary);
  const price = resolvePrice(productData, pricing);
  const stockCount = toNumber(inventory.count ?? inventory.quantity ?? inventory.stock ?? productData.stock);
  const category = toText(productData.category).trim();

  return {
    ...productData,
    slug,
    name: toText(productData.name ?? productData.title, 'Untitled product'),
    image,
    gallery: resolveGallery(media, image),
    price,
    priceValue: resolvePriceValue(productData, pricing, price),
    shortDescription,
    description: toText(
      productData.description ?? productData.longDescription ?? productData.body,
      shortDescription,
    ),
    category,
    featured: toBoolean(productData.featured),
    inStock: resolveInStock(productData, inventory),
    stockCount,
    sku: toText(productData.sku ?? productData.SKU),
    categories: uniqueValues([
      category,
      ...resolveTextList(productData.categories ?? productData.tags),
    ]),
    checkoutUrl: resolveCheckoutUrl(productData, pricing),
  };
}

export function normalizeStoreProductCard(product) {
  const normalizedProduct = normalizeStoreProduct(product);

  if (!normalizedProduct) {
    return null;
  }

  return {
    slug: normalizedProduct.slug,
    name: normalizedProduct.name,
    image: normalizedProduct.image,
    price: normalizedProduct.price,
    priceValue: normalizedProduct.priceValue,
    shortDescription: normalizedProduct.shortDescription,
    featured: normalizedProduct.featured,
    inStock: normalizedProduct.inStock,
  };
}

export function findStoreProductBySlug(products, routeSlug) {
  const requestedSlug = normalizeStoreRouteSlug(routeSlug);

  if (!requestedSlug) {
    return null;
  }

  return (
    asArray(products)
      .map((product) => normalizeStoreProduct(product))
      .filter(Boolean)
      .find((product) => product.slug === requestedSlug) ?? null
  );
}

export function buildStoreProductPath(product) {
  const normalizedProduct = normalizeStoreProduct(product);

  return normalizedProduct?.slug ? `/store/${encodeURIComponent(normalizedProduct.slug)}` : '/store';
}

export function rememberStorePaymentLinkReturn(context = {}) {
  const storage = getStoreCheckoutStorage();

  if (!storage) {
    return;
  }

  const payload = {
    checkoutMode: STORE_CHECKOUT_MODE_PAYMENT_LINK,
    checkoutUrl: toText(context.checkoutUrl),
    productName: toText(context.productName),
    productSlug: toText(context.productSlug),
    createdAt: Date.now(),
  };

  try {
    storage.setItem(STORE_PAYMENT_LINK_RETURN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage write failures so checkout can continue.
  }
}

export function clearStorePaymentLinkReturn() {
  const storage = getStoreCheckoutStorage();

  if (!storage) {
    return;
  }

  try {
    storage.removeItem(STORE_PAYMENT_LINK_RETURN_STORAGE_KEY);
  } catch {
    // Ignore storage removal failures.
  }
}

export function consumeStorePaymentLinkReturn() {
  const storage = getStoreCheckoutStorage();

  if (!storage) {
    return null;
  }

  try {
    const rawValue = storage.getItem(STORE_PAYMENT_LINK_RETURN_STORAGE_KEY);

    storage.removeItem(STORE_PAYMENT_LINK_RETURN_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    const payload = JSON.parse(rawValue);
    const createdAt = toNumber(payload?.createdAt) ?? 0;

    if (!createdAt || Date.now() - createdAt > STORE_PAYMENT_LINK_RETURN_MAX_AGE_MS) {
      return null;
    }

    if (toText(payload?.checkoutMode) !== STORE_CHECKOUT_MODE_PAYMENT_LINK) {
      return null;
    }

    return {
      checkoutMode: STORE_CHECKOUT_MODE_PAYMENT_LINK,
      checkoutUrl: toText(payload?.checkoutUrl),
      productName: toText(payload?.productName),
      productSlug: toText(payload?.productSlug),
    };
  } catch {
    return null;
  }
}

export async function fetchStoreData() {
  const response = await fetch(buildApiUrl('/store'));

  if (!response.ok) {
    throw new Error('Failed to fetch store products');
  }

  const payload = await response.json().catch(() => ({}));
  const { meta, products } = unwrapStorePayload(payload);
  const normalizedProducts = products.map((product) => normalizeStoreProduct(product)).filter(Boolean);

  return {
    meta,
    products: normalizedProducts,
    productCards: normalizedProducts.map((product) => normalizeStoreProductCard(product)).filter(Boolean),
  };
}

export async function fetchStoreProduct(routeSlug) {
  const normalizedSlug = normalizeStoreRouteSlug(routeSlug);

  if (!normalizedSlug) {
    throw new Error('Product not found');
  }

  const response = await fetch(buildApiUrl(`/store/${encodeURIComponent(normalizedSlug)}`));

  if (response.ok) {
    const payload = await response.json().catch(() => ({}));
    const { product } = unwrapStoreProductPayload(payload);

    if (product) {
      return product;
    }
  }

  const { products } = await fetchStoreData();
  const product = findStoreProductBySlug(products, normalizedSlug);

  if (!product) {
    throw new Error('Product not found');
  }

  return product;
}

export async function createStoreCheckoutSession({ slug, quantity = 1, email = '' }) {
  const response = await fetch(buildApiUrl('/checkout/session'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      slug,
      quantity,
      email,
    }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      toText(payload.error, 'Failed to start secure checkout')
    );
  }

  return {
    orderId: toText(payload.orderId) || null,
    checkoutMode: toText(payload.checkoutMode, STORE_CHECKOUT_MODE_HOSTED_SESSION),
    url: toText(payload.url ?? payload.checkoutUrl ?? payload.checkout_url),
  };
}

export async function fetchStoreCheckoutOrder({ orderId, sessionId }) {
  const params = new URLSearchParams({
    session_id: toText(sessionId),
  });
  const response = await fetch(
    buildApiUrl(`/checkout/orders/${encodeURIComponent(toText(orderId))}?${params}`)
  );
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(toText(payload.error, 'Failed to fetch checkout order'));
  }

  return normalizeStoreCheckoutOrder(payload);
}
